import { z } from "zod";
import { InvitationInfoOut, RegisterIn, RegisterOut } from "@/lib/api/schemas/registration";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

/** Public registration endpoints — reached from an emailed invitation link, before a token exists. */
class RegistrationEndpoint {
  public static readonly basePath = "register";
  public static readonly schemas = {
    getInvitation: {
      response: InvitationInfoOut,
    },
    register: {
      body: RegisterIn,
      response: RegisterOut,
    },
  };

  constructor(private api: Auth) {}

  /** Fetch the invitation behind a registration token (GET register/{token}/). */
  public getInvitation(
    token: string,
  ): ActionReturnType<z.infer<typeof RegistrationEndpoint.schemas.getInvitation.response>> {
    return request({
      log: ["Registration", "getInvitation"],
      method: "get",
      path: [RegistrationEndpoint.basePath, token],
      response: RegistrationEndpoint.schemas.getInvitation.response,
      skipAuth: true,
      localErrorHandling: true,
    });
  }

  /** Complete registration with a password; the response carries the JWT for the new session. */
  public async register(
    body: z.input<typeof RegistrationEndpoint.schemas.register.body>,
  ): ActionReturnType<z.infer<typeof RegistrationEndpoint.schemas.register.response>> {
    const bodyValidated = validateInput(RegistrationEndpoint.schemas.register.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Registration", "register"],
      method: "post",
      path: [RegistrationEndpoint.basePath],
      json: bodyValidated.data,
      response: RegistrationEndpoint.schemas.register.response,
      skipAuth: true,
      localErrorHandling: true,
    });
  }
}

export { RegistrationEndpoint };
